/**
 * Company-name matching for pickers that pre-fill from free text.
 *
 * The owner types "Century 21 - Abrams & Associates" on the Me Card while
 * the CRM holds "Century 21 Abrams & Associates": punctuation, case and
 * spacing differ, the company does not. Names are compared in a
 * normalised form so the existing company is found instead of a
 * duplicate being created.
 */

/** Lowercase, every run of characters that is not a letter or digit collapsed to one space, trimmed. */
export function normalizeCompanyName(name: string | null | undefined): string {
    return (name || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

/** True when the query is blank or its normalised form appears in the candidate's. */
export function companyNameIncludes(candidate: string | null | undefined, query: string): boolean {
    const wanted = normalizeCompanyName(query);
    return !wanted || normalizeCompanyName(candidate).includes(wanted);
}

/**
 * The company a typed name refers to: the one whose normalised name is the
 * same, else the only one whose normalised name contains the typed one or
 * is contained by it. Undefined when nothing, or more than one, could be
 * meant — the caller then lets the user pick.
 */
export function findCompanyByName<T>(
    companies: readonly T[],
    nameOf: (company: T) => string | null | undefined,
    typed: string,
): T | undefined {
    const wanted = normalizeCompanyName(typed);
    if (!wanted) return undefined;
    const named = companies.map((company) => ({ company, name: normalizeCompanyName(nameOf(company)) }));
    const exact = named.find(({ name }) => name === wanted);
    if (exact) return exact.company;
    const partial = named.filter(({ name }) => name !== '' && (name.includes(wanted) || wanted.includes(name)));
    return partial.length === 1 ? partial[0]?.company : undefined;
}
